import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { AdminHeader, AdminPage, StateBadge } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function PilList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pils?include_unpublished=true&limit=200${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setItems(data.items);
    } catch { toast.error("Failed to load PILs"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = items.filter((p) => tab === "all" || p.state === tab);
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/pils/${p.id}`); toast.success("PIL deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <>
      <AdminHeader title="PIL Management" subtitle={`${items.length} matters`} actions={
        <Button asChild className="bg-npst-navy hover:bg-npst-navyDeep text-white"><Link to="/admin/pils/new" data-testid="add-pil-btn"><Plus size={16} className="mr-1.5" /> Add PIL</Link></Button>
      } />
      <AdminPage>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              {["all", "draft", "published", "archived"].map((t) => <TabsTrigger key={t} value={t} data-testid={`pil-tab-${t}`} className="capitalize">{t}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 w-full sm:w-72">
            <div className="flex-1 flex items-center gap-2 px-3 border border-slate-200 rounded-md bg-white">
              <Search size={15} className="text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="flex-1 py-2 text-sm outline-none" data-testid="pil-admin-search" />
            </div>
          </form>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 w-12">#</th><th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">PIL Code</th><th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">State</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400 animate-pulse">Loading…</td></tr>
                : filtered.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No PILs in this view.</td></tr>
                : filtered.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pil-item-${p.pil_code}`}>
                    <td className="px-4 py-3 font-serif text-slate-400">{String(p.seq).padStart(2, "0")}</td>
                    <td className="px-4 py-3 font-medium text-npst-ink max-w-md truncate">{p.title}{p.ai_generated && <span className="ml-2 text-[9px] bg-purple-50 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded uppercase">AI</span>}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.pil_code}</td>
                    <td className="px-4 py-3 text-xs">{p.status}</td>
                    <td className="px-4 py-3"><StateBadge state={p.state} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-3 text-slate-400">
                        {p.state === "published" && <a href={`/pil/${p.pil_code.toLowerCase()}`} target="_blank" rel="noopener noreferrer" className="hover:text-npst-navy" title="View"><Eye size={16} /></a>}
                        <Link to={`/admin/pils/${p.id}`} className="hover:text-npst-navy" title="Edit" data-testid={`edit-pil-${p.pil_code}`}><Pencil size={16} /></Link>
                        <button onClick={() => del(p)} className="hover:text-red-600" title="Delete" data-testid={`delete-pil-${p.pil_code}`}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </AdminPage>
    </>
  );
}
